import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import PayrollHeroAnimatedText from './PayrollHeroAnimatedText';
import Breadcrumbs from '../navigation/Breadcrumbs';
import { buildServiceBreadcrumbs } from '../navigation/breadcrumbUtils';
import { getBreadcrumbSchema } from '../navigation/breadcrumbSchema';
import SEO from '../seo/SEO';
import './Payroll.css';

const Payroll = () => {
  const location = useLocation();
  const servicesIndexView = location.state?.servicesIndexView;
  const backPath =
    servicesIndexView === 'payroll' ||
    location.pathname.startsWith('/services/payroll/')
      ? '/services/payroll'
      : servicesIndexView
        ? '/services'
        : '/';

  const breadcrumbItems = buildServiceBreadcrumbs('payroll', 'Payroll Services');
  const breadcrumbSchema = getBreadcrumbSchema(breadcrumbItems);

  return (
    <div className="standard-page-wrapper">
      <SEO schema={breadcrumbSchema} />
      <div className="standard-page-container">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="standard-back-link">
          <Link
            to={backPath}
            state={servicesIndexView ? { servicesIndexView } : undefined}
          >
            {servicesIndexView ? '\u2190 Back' : '\u2190 Back to Home'}
          </Link>
        </div>

        <div className="standard-page-hero">
          <PayrollHeroAnimatedText
            as="h1"
            text="Payroll Services"
            className="payroll-hero-title"
            variant="from-left"
            threshold={0}
            rootMargin="0px"
            step={68}
          />
          <p>
            BlackRoth provides secure, scalable, and compliant Payroll Services
            that streamline salary processing, statutory compliance, and
            workforce compensation management for modern organizations.
          </p>
        </div>

        <section className="standard-page-section">
          <div className="service-cards-grid">
            <article className="service-card payroll-static-card">
              <h3>Payroll Services</h3>
              <p>
                End-to-end payroll operations covering salary processing,
                compliance, reporting, and employee management.
              </p>
            </article>

            <article className="service-card payroll-static-card">
              <h3>Payroll Software</h3>
              <p>
                Intelligent payroll software with automation, analytics,
                integrations, and compliance-ready workflows.
              </p>
            </article>

            <article className="service-card payroll-static-card">
              <h3>Payroll Processing</h3>
              <p>
                Accurate, timely, and regulation-compliant payroll processing
                services for businesses of any scale.
              </p>
            </article>
          </div>
        </section>

        <section className="standard-page-section">
          <h2>Overview</h2>
          <p>
            Payroll management is a critical business function that requires
            accuracy, security, and compliance. BlackRoth delivers end-to-end
            payroll solutions that automate payroll operations, reduce manual
            errors, and ensure timely salary disbursement.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Core Capabilities</h2>
          <p>
            Our payroll services are designed to handle complex payroll
            structures, multi-location operations, and regulatory requirements
            while maintaining data integrity and operational efficiency.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Services We Offer</h2>
          <ul className="standard-service-list">
            <li>End-to-End Payroll Processing</li>
            <li>Salary Calculation & Disbursement</li>
            <li>Statutory Compliance & Tax Management</li>
            <li>Employee Payslips & Reports</li>
            <li>Attendance & Leave Integration</li>
            <li>Payroll Automation & Optimization</li>
            <li>Secure Payroll Data Management</li>
            <li>Custom Payroll Solutions</li>
          </ul>
        </section>

        <section className="standard-page-section">
          <h2>Business Use Cases</h2>
          <p>
            BlackRoth's Payroll Services support startups, SMEs, and large
            enterprises by simplifying payroll operations, ensuring regulatory
            compliance, and improving employee satisfaction.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Technology & Integration</h2>
          <p>
            Our payroll solutions integrate seamlessly with HRMS, attendance
            systems, accounting platforms, and banking systems using secure
            APIs and modern cloud-based architectures.
          </p>
        </section>

        <section className="standard-page-section">
          <h2>Security & Compliance</h2>
          <p>
            BlackRoth follows strict security and compliance standards to
            protect sensitive payroll data, including encryption, access
            control, audit logging, and adherence to statutory regulations.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Payroll;
